import { useBroadcastDeviceDialog } from '@/stores/useBroadcastDeviceDialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useBroadcastDeviceById } from '@/hooks/useBroadcastDeviceById'
import { PeopleFlowDevice } from '@magnetar/admin-api-interfaces'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useEffect, useState, useMemo } from 'react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog'
import { usePeopleFlowDeviceRecords } from '@/hooks/usePeopleFlowDeviceRecords'
import { format, subDays } from 'date-fns'
import { DateRange } from 'react-day-picker'
import ClinicPatientFlowInfo from './BroadcastDevicePatientFlowInfo'
import BroadcastDeviceAdPlayInfo from './BroadcastDeviceAdPlayInfo'
import BroadcastDevicePlaybackDurationInfo from './BroadcastDevicePlaybackDurationInfo'

// Define types for the device data structure
interface ExaminationRoom {
  name: string
  examination_room_id: number
}

interface BroadcastDeviceExaminationRoom {
  examination_room: ExaminationRoom
  examination_room_id: number
}

interface Clinic {
  clinic_id: number
  clinic_name: string
}

export interface ExtendedBroadcastDevice {
  name: string
  broadcast_device_id: number
  clinic_id: number
  broadcast_type: string
  is_active: boolean
  created_at: Date
  clinic?: Clinic
  examination_rooms?: BroadcastDeviceExaminationRoom[]
  people_flow_devices?: PeopleFlowDevice[]
}

export function BroadcastDeviceDialog() {
  const { isOpen, currentBroadcastDevice, closeDialog } = useBroadcastDeviceDialog()
  const [activeTab, setActiveTab] = useState<string>('')
  const [isExaminationRoomsDialogOpen, setIsExaminationRoomsDialogOpen] = useState(false)
  const [dateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  })
  const [mainActiveTab, setMainActiveTab] = useState<'flow' | 'adplay' | 'playback'>('flow')

  // Fetch the latest broadcast device data using the hook
  const { device, isLoading, isError } = useBroadcastDeviceById(
    currentBroadcastDevice?.broadcast_device_id,
  ) as {
    device?: ExtendedBroadcastDevice
    isLoading: boolean
    isError: unknown
    mutate: () => Promise<unknown>
  }

  // Set initial active tab when device data is loaded
  useEffect(() => {
    if (device?.people_flow_devices && device.people_flow_devices.length > 0 && !activeTab) {
      setActiveTab(device.people_flow_devices[0].people_flow_device_id.toString())
    }
  }, [device, activeTab])

  // Prepare daily records parameters for the active tab
  const dailyRecordsParams = useMemo(() => {
    if (!isOpen || !activeTab) return { peopleFlowDeviceId: undefined }
    return {
      peopleFlowDeviceId: parseInt(activeTab),
      interval: 'daily' as const,
      startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      pageSize: 100, // Get more records for better visualization
    }
  }, [isOpen, activeTab, dateRange.from, dateRange.to])

  // Fetch daily records
  const { mutate: refreshDailyRecords } = usePeopleFlowDeviceRecords(dailyRecordsParams)

  // Prepare hourly records parameters
  const hourlyRecordsParams = useMemo(() => {
    if (!isOpen || !activeTab) return { peopleFlowDeviceId: undefined }
    return {
      peopleFlowDeviceId: parseInt(activeTab),
      interval: 'hourly' as const,
      startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      pageSize: 100, // Get more records for better visualization
    }
  }, [isOpen, activeTab, dateRange.from, dateRange.to])

  // Fetch hourly records
  const { mutate: refreshHourlyRecords } = usePeopleFlowDeviceRecords(hourlyRecordsParams)

  // Refresh data when dialog opens or active tab changes
  useEffect(() => {
    if (isOpen && activeTab) {
      refreshDailyRecords()
      refreshHourlyRecords()
    }
  }, [isOpen, activeTab, refreshDailyRecords, refreshHourlyRecords])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={closeDialog}>
        <DialogContent className="w-[80vw] max-w-[90vw] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>廣播裝置設定</DialogTitle>
              <DialogDescription>
                您正在設定廣播裝置: {device?.name || currentBroadcastDevice?.name} (ID:{' '}
                {device?.broadcast_device_id || currentBroadcastDevice?.broadcast_device_id})
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
              ) : isError ? (
                <div className="text-center text-destructive py-20">
                  <p>載入裝置資料時發生錯誤，請重試</p>
                </div>
              ) : (
                <Tabs
                  value={mainActiveTab}
                  onValueChange={(value: string) =>
                    setMainActiveTab(value as 'flow' | 'adplay' | 'playback')
                  }
                  className="w-full h-full flex flex-col"
                >
                  <TabsList className="mb-4">
                    <TabsTrigger value="flow">人流</TabsTrigger>
                    <TabsTrigger value="adplay">廣告播放資料</TabsTrigger>
                    <TabsTrigger value="playback">播放時長</TabsTrigger>
                  </TabsList>
                  <div className="flex-1 overflow-y-scroll">
                    <TabsContent value="flow" className="h-full overflow-y-auto">
                      {device && <ClinicPatientFlowInfo clinicId={device.clinic_id} />}
                    </TabsContent>
                    <TabsContent value="adplay" className="h-full overflow-y-auto">
                      {device && <BroadcastDeviceAdPlayInfo device={device} />}
                    </TabsContent>
                    <TabsContent value="playback" className="h-full overflow-y-auto">
                      {device && (
                        <BroadcastDevicePlaybackDurationInfo
                          broadcastDeviceId={device.broadcast_device_id}
                        />
                      )}
                    </TabsContent>
                  </div>
                </Tabs>
              )}
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={closeDialog}>
                關閉
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* 診間列表對話框 */}
      <AlertDialog
        open={isExaminationRoomsDialogOpen}
        onOpenChange={setIsExaminationRoomsDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>診間列表</AlertDialogTitle>
            <AlertDialogDescription>此廣播裝置已掛載以下診間</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            {device?.examination_rooms && device.examination_rooms.length > 0 ? (
              <div className="space-y-2">
                {device.examination_rooms.map((er) => (
                  <div
                    key={er.examination_room_id}
                    className="p-3 border rounded-md flex justify-between items-center"
                  >
                    <div>
                      <div className="font-medium">{er.examination_room?.name}</div>
                      <div className="text-sm text-muted-foreground">
                        ID: {er.examination_room_id}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">沒有掛載任何診間</div>
            )}
          </div>
          <AlertDialogFooter>
            <Button onClick={() => setIsExaminationRoomsDialogOpen(false)}>關閉</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default BroadcastDeviceDialog
